import memoize from 'lodash.memoize';
import * as technical from '../3.x/iiif/technical';
import * as descriptive from '../3.x/iiif/descriptive';
import * as linking from '../3.x/iiif/linking';
import * as structural from '../3.x/iiif/structural';
import { createSelector } from 'reselect';
import { getAllCanvases, getAllResources, getAllServices, getAllContentResources, getAllManifests, getAllCollections, getAllAnnotationPages } from '../all';
import mapById from '../../utility/mapById';
import mapAllById from '../../utility/mapAllById';
import mapAllResources from '../../utility/mapAllResources';
import mapByIdOrId from '../../utility/mapByIdOrId';

var collection = memoize(function (selector) {
  /**
   * Technical properties
   *
   * - getId
   * - getType
   * - getViewingDirection
   * - getBehavior
   */
  var getId = createSelector(selector, technical.getId);

  var getType = createSelector(selector, technical.getType);

  var getViewingDirection = createSelector(selector, technical.getViewingDirection);

  var getBehavior = createSelector(selector, technical.getBehavior);

  /**
   * Descriptive Properties
   *
   * - getLabel
   * - getMetadata
   * - getSummary
   * - getThumbnail
   * - getPosterCanvas
   * - getRequiredStatement
   * - getRights
   * - getNavDate
   */
  var getLabel = createSelector(selector, descriptive.getLabel);

  var getMetadata = createSelector(selector, descriptive.getMetadata);

  var getSummary = createSelector(selector, descriptive.getSummary);

  var getThumbnailId = createSelector(selector, descriptive.getThumbnailId);

  var getThumbnail = createSelector(getThumbnailId, getAllContentResources, mapByIdOrId);

  var getPosterCanvasId = createSelector(selector, descriptive.getPosterCanvas);

  var getPosterCanvas = createSelector(getPosterCanvasId, getAllCanvases, mapById);

  var getRequiredStatement = createSelector(selector, descriptive.getRequiredStatement);

  var getRights = createSelector(selector, descriptive.getRights);

  var getNavDate = createSelector(selector, descriptive.getNavDate);

  /**
   * Linking properties
   *
   * - getSeeAlso
   * - getService
   * - getLogo
   * - getHomepage
   * - getRendering
   * - getPartOf
   */
  // @todo Clarify with specification, since this can technically be anything in this model.
  var getSeeAlsoIds = createSelector(selector, linking.getSeeAlso);
  var getSeeAlso = createSelector(getSeeAlsoIds, getAllContentResources, mapAllById);

  var getServiceIds = createSelector(selector, linking.getService);
  var getService = createSelector(getServiceIds, getAllServices, mapAllById);

  var getLogoIds = createSelector(selector, linking.getLogo);
  var getLogo = createSelector(getLogoIds, getAllContentResources, mapAllById);

  var getHomepageId = createSelector(selector, linking.getHomepage);
  var getHomepage = createSelector(getHomepageId, getAllContentResources, mapById);

  var getRenderingIds = createSelector(selector, linking.getRendering);
  var getRendering = createSelector(getRenderingIds, getAllContentResources, mapAllById);

  var getPartOfId = createSelector(selector, linking.getPartOf);
  var getPartOf = createSelector(getPartOfId, getAllResources, mapAllResources);

  /**
   * Structural Properties
   * - getItems
   * - getManifests (non-standard)
   * - getCollections (non-standard)
   * - getAnnotations
   */
  var getItemIds = createSelector(selector, structural.getItems);
  var getItems = createSelector(getItemIds, getAllManifests, getAllCollections, function (items, allManifests, allCollections) {
    return items.map(function (item) {
      return item.schema === 'manifest' ? allManifests[item.id] : allCollections[item.id];
    });
  });

  var getManifestIds = createSelector(getItemIds, function (items) {
    return items.map(function (item) {
      return item.schema === 'manifest' ? item.id : null;
    }).filter(Boolean);
  });
  var getManifests = createSelector(getManifestIds, getAllManifests, mapById);

  var getCollectionIds = createSelector(getItemIds, function (items) {
    return items.map(function (item) {
      return item.schema === 'collection' ? item.id : null;
    }).filter(Boolean);
  });
  var getCollections = createSelector(getCollectionIds, getAllCollections, mapById);

  // @todo kinda misleading, as these are annotation pages.
  var getAnnotationIds = createSelector(selector, structural.getAnnotations);
  var getAnnotations = createSelector(getAnnotationIds, getAllAnnotationPages, mapAllById);

  return {
    collection: collection,
    getId: getId,
    getType: getType,
    getViewingDirection: getViewingDirection,
    getBehavior: getBehavior,
    getLabel: getLabel,
    getMetadata: getMetadata,
    getSummary: getSummary,
    getThumbnailId: getThumbnailId,
    getThumbnail: getThumbnail,
    getPosterCanvasId: getPosterCanvasId,
    getPosterCanvas: getPosterCanvas,
    getRequiredStatement: getRequiredStatement,
    getRights: getRights,
    getNavDate: getNavDate,
    getSeeAlsoIds: getSeeAlsoIds,
    getSeeAlso: getSeeAlso,
    getServiceIds: getServiceIds,
    getService: getService,
    getLogoIds: getLogoIds,
    getLogo: getLogo,
    getHomepageId: getHomepageId,
    getHomepage: getHomepage,
    getRenderingIds: getRenderingIds,
    getRendering: getRendering,
    getPartOfId: getPartOfId,
    getPartOf: getPartOf,
    getItemIds: getItemIds,
    getItems: getItems,
    getManifestIds: getManifestIds,
    getManifests: getManifests,
    getCollectionIds: getCollectionIds,
    getCollections: getCollections,
    getAnnotationIds: getAnnotationIds,
    getAnnotations: getAnnotations
  };
});

export default collection;